import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { User, Building2, Loader2, History } from "lucide-react";
import { format } from "date-fns";
import { getAssignmentHistory } from "../../services/crmServices/assignmentLogService";
import { getFullName } from "../../services/common/util";

export function LeadHistory({ leadId, className = "" }) {
    const [assignmentHistory, setAssignmentHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (leadId) {
            fetchHistory();
        }
    }, [leadId]);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const assignmentsResponse = await getAssignmentHistory(leadId).catch(err => {
                console.error("Error fetching assignment history:", err);
                return { data: [] };
            });

            setAssignmentHistory(assignmentsResponse?.data || []);
        } catch (error) {
            console.error("Error fetching history:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatAgentName = (agent) => {
        if (!agent) return "Unassigned";
        return getFullName(agent) || "Unknown";
    };

    const formatDeskName = (desk) => {
        if (!desk) return "Unassigned";
        return desk.name || "Unknown Desk";
    };

    if (loading) {
        return (
            <Card className={className}>
                <CardContent className="p-6">
                    <div className="flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                        <span className="ml-2 text-gray-600">Loading history...</span>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Helper function to normalize IDs for comparison (handles ObjectId, populated objects, null)
    const normalizeId = (value) => {
        if (!value) return null;
        if (typeof value === 'string') return value;
        if (value._id) return value._id.toString();
        if (value.toString) return value.toString();
        return null;
    };

    return (
        <Card className={`${className} flex flex-col overflow-hidden`}>
            <CardHeader className="flex-shrink-0">
                <CardTitle className="flex items-center">
                    <History className="h-5 w-5 mr-2" />
                    Assignment History
                </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col p-0 min-h-0 overflow-hidden">
                {/* Assignment History - Fixed height to show ~3 items, scrollable if more */}
                <div className="overflow-y-auto p-4 space-y-3" style={{ maxHeight: '400px' }}>
                    {assignmentHistory.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                            <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>No assignment history available</p>
                        </div>
                    ) : (
                        assignmentHistory.map((assignment) => {
                            // Normalize IDs for proper comparison
                            const oldAgentId = normalizeId(assignment.oldAgent);
                            const newAgentId = normalizeId(assignment.newAgent);
                            const oldDeskId = normalizeId(assignment.oldDesk);
                            const newDeskId = normalizeId(assignment.newDesk);
                            const oldFloorManagerId = normalizeId(assignment.oldFloorManager);
                            const newFloorManagerId = normalizeId(assignment.newFloorManager);

                            const agentChanged = oldAgentId !== newAgentId;
                            const deskChanged = oldDeskId !== newDeskId;
                            const floorManagerChanged = oldFloorManagerId !== newFloorManagerId;

                            // Show changes if any field changed, or if we have assignment data
                            const hasChanges = agentChanged || deskChanged || floorManagerChanged;
                            const hasAnyData = assignment.oldAgent || assignment.newAgent || 
                                             assignment.oldDesk || assignment.newDesk ||
                                             assignment.oldFloorManager || assignment.newFloorManager;

                            return (
                                <div
                                    key={assignment._id}
                                    className="p-3 border rounded-lg hover:bg-gray-50"
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center space-x-2">
                                            <div className="p-2 bg-purple-100 rounded-full flex-shrink-0">
                                                <User className="h-4 w-4 text-purple-600" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900">
                                                    Changed by: {formatAgentName(assignment.changedBy)}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {format(
                                                        new Date(assignment.createdAt),
                                                        "MMM dd, yyyy HH:mm:ss"
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {(hasChanges || hasAnyData) && (
                                        <div className="ml-10 space-y-2 text-sm">
                                            {/* Always show agent if we have data, or if it changed */}
                                            {(agentChanged || assignment.oldAgent || assignment.newAgent) && (
                                                <div className="flex items-center space-x-2 flex-wrap">
                                                    <User className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                                    <span className="text-gray-600">Agent:</span>
                                                    <span className="text-red-600 line-through">
                                                        {formatAgentName(assignment.oldAgent)}
                                                    </span>
                                                    <span className="text-gray-400">→</span>
                                                    <span className="text-green-600 font-medium">
                                                        {formatAgentName(assignment.newAgent)}
                                                    </span>
                                                </div>
                                            )}
                                            {/* Always show desk if we have data, or if it changed */}
                                            {(deskChanged || assignment.oldDesk || assignment.newDesk) && (
                                                <div className="flex items-center space-x-2 flex-wrap">
                                                    <Building2 className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                                    <span className="text-gray-600">Desk:</span>
                                                    <span className="text-red-600 line-through">
                                                        {formatDeskName(assignment.oldDesk)}
                                                    </span>
                                                    <span className="text-gray-400">→</span>
                                                    <span className="text-green-600 font-medium">
                                                        {formatDeskName(assignment.newDesk)}
                                                    </span>
                                                </div>
                                            )}
                                            {/* Always show floor manager if we have data, or if it changed */}
                                            {(floorManagerChanged || assignment.oldFloorManager || assignment.newFloorManager) && (
                                                <div className="flex items-center space-x-2 flex-wrap">
                                                    <User className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                                    <span className="text-gray-600">Floor Manager:</span>
                                                    <span className="text-red-600 line-through">
                                                        {formatAgentName(assignment.oldFloorManager)}
                                                    </span>
                                                    <span className="text-gray-400">→</span>
                                                    <span className="text-green-600 font-medium">
                                                        {formatAgentName(assignment.newFloorManager)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

